import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Battery, Radio, Trophy, X, Zap } from "lucide-react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
} from "recharts";
import {
  SEVERITY_BANDS,
  SEVERITY_COLORS,
  SEVERITY_TEXT,
  SEVERITY_LEVELS,
  bucketCounts,
  severityIndex,
} from "utils/caseLiveStats";
import { fmtShortMac, fmtTime } from "../parts";
import {
  Avatar,
  BatteryBar,
  Segmented,
  SeverityBar,
  SeverityLegend,
  Sparkline,
  StatusPill,
  gText,
  useAgo,
} from "./shared";

const STATUS_RANK = { silent: 0, never: 1, quiet: 2, live: 3, ended: 4 };

const sorters = {
  attention: (a, b) =>
    STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
    (a.battery ?? 100) - (b.battery ?? 100),
  impacts: (a, b) => b.count - a.count,
  hardest: (a, b) => b.maxG - a.maxG,
  name: (a, b) => String(a.name).localeCompare(String(b.name)),
};

/** A card per player: are they being heard, how much contact, hardest hit. */
export const PlayerGrid = ({ stats, replay, now, onSelect, selected }) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState(replay ? "impacts" : "attention");
  const players = useMemo(
    () =>
      [...stats.players].sort(
        (a, b) => a.removed - b.removed || sorters[sort](a, b)
      ),
    [stats.players, sort]
  );
  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[15px] font-semibold text-[#111827]">
          {t("caseDashboard.live.roster")}
          <span className="ml-2 text-[13px] font-normal text-[#9CA3AF]">
            {stats.active.length}
          </span>
        </h2>
        <Segmented
          value={sort}
          onChange={setSort}
          options={[
            ...(replay
              ? []
              : [
                  {
                    value: "attention",
                    label: t("caseDashboard.live.sort.attention"),
                  },
                ]),
            { value: "impacts", label: t("caseDashboard.live.sort.impacts") },
            { value: "hardest", label: t("caseDashboard.live.sort.hardest") },
            { value: "name", label: t("caseDashboard.live.sort.name") },
          ]}
        />
      </div>
      <motion.div
        layout
        className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"
      >
        <AnimatePresence initial={false}>
          {players.map((p) => (
            <PlayerCard
              key={p.key}
              player={p}
              stats={stats}
              replay={replay}
              now={now}
              selected={selected === p.key}
              onClick={() => onSelect(p.key)}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
};

const BORDER = {
  silent: "border-[#FECACA]",
  never: "border-[#FECACA]",
  quiet: "border-[#FDE3C8]",
};

const PlayerCard = ({ player: p, stats, replay, now, selected, onClick }) => {
  const { t } = useTranslation();
  const ago = useAgo();
  // Flash when this player takes a new hit.
  const prev = useRef(p.count);
  const [flash, setFlash] = useState(0);
  useEffect(() => {
    if (p.count > prev.current) setFlash((f) => f + 1);
    prev.current = p.count;
  }, [p.count]);

  const from = stats.started ?? stats.end - 3600e3;
  const activity = useMemo(
    () => bucketCounts(p.times, from, stats.end, 24),
    [p.times, from, stats.end]
  );

  return (
    <motion.button
      type="button"
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: p.removed ? 0.55 : 1, scale: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl border bg-white p-4 text-left transition-shadow duration-150 hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] ${
        selected ? "ring-2 ring-[#E07A3A] ring-offset-1" : ""
      } ${BORDER[p.status] || "border-[#F3F4F6]"}`}
    >
      {flash > 0 && (
        <motion.span
          key={flash}
          className="pointer-events-none absolute inset-0 rounded-xl"
          initial={{
            boxShadow: "inset 0 0 0 2px rgba(224,122,58,0.9)",
            backgroundColor: "rgba(254,243,231,0.9)",
          }}
          animate={{
            boxShadow: "inset 0 0 0 2px rgba(224,122,58,0)",
            backgroundColor: "rgba(254,243,231,0)",
          }}
          transition={{ duration: 1.6 }}
        />
      )}
      <div className="relative flex items-start gap-3">
        <Avatar name={p.name} color={p.color} size={36} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-semibold text-[#111827]">
              {p.name || t("caseDashboard.live.unknownPlayer")}
            </span>
            {p.removed ? (
              <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]">
                {t("caseDashboard.live.removed")}
              </span>
            ) : (
              !replay && <StatusPill status={p.status} />
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-[#9CA3AF]">
            <span className="font-mono" title={p.mac}>
              #{p.wearable || fmtShortMac(p.mac)}
            </span>
            {!replay && !p.removed && (
              <>
                <span>·</span>
                <Radio size={10} />
                <span
                  className={
                    p.status === "silent" ? "font-medium text-[#DC2626]" : ""
                  }
                >
                  {ago(p.lastContact, now)}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="relative mt-3 flex items-end justify-between gap-3">
        <div>
          <motion.span
            key={p.count}
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            className="block origin-left text-[24px] font-bold tabular-nums leading-none text-[#111827]"
          >
            {p.count}
          </motion.span>
          <span className="text-[11px] text-[#6B7280]">
            {t("caseDashboard.live.impactsLabel")}
          </span>
        </div>
        <div className="text-right">
          <span
            className="block text-[15px] font-bold tabular-nums leading-none"
            style={{
              color: p.count ? SEVERITY_TEXT[severityIndex(p.maxG)] : "#D1D5DB",
            }}
          >
            {p.count ? gText(t, p.maxG) : "—"}
          </span>
          <span className="text-[11px] text-[#6B7280]">
            {t("caseDashboard.live.hardestLabel")}
          </span>
        </div>
      </div>

      <Sparkline
        values={activity}
        color={p.color}
        height={22}
        className="relative mt-2"
      />
      <SeverityBar counts={p.severity} className="relative mt-2" />

      <div className="relative mt-3 flex items-center justify-between">
        <BatteryBar pct={p.battery} />
        {p.lastImpactAt && (
          <span className="text-[11px] text-[#9CA3AF]">
            {t("caseDashboard.live.lastHit", {
              time: replay
                ? fmtTime(new Date(p.lastImpactAt))
                : ago(p.lastImpactAt, now),
            })}
          </span>
        )}
      </div>
    </motion.button>
  );
};

/** Who's taking the most contact, split by severity. */
export const ImpactMix = ({ stats, onSelect, selected }) => {
  const { t } = useTranslation();
  const rows = [...stats.players]
    .filter((p) => p.count)
    .sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...rows.map((p) => p.count));
  return (
    <div className="rounded-xl border border-[#F3F4F6] bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#F3F4F6] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#111827]">
          {t("caseDashboard.live.perPlayer")}
        </h2>
        <SeverityLegend counts={stats.severity} />
      </div>
      <div className="space-y-1 p-3">
        {rows.length === 0 && (
          <p className="px-1 py-6 text-center text-[13px] text-[#9CA3AF]">
            {t("caseDashboard.live.noImpacts")}
          </p>
        )}
        {rows.map((p) => (
          <button
            type="button"
            key={p.key}
            onClick={() => onSelect(p.key)}
            className={`grid w-full grid-cols-[140px_1fr_48px] items-center gap-3 rounded-lg px-2 py-1.5 text-left transition duration-150 hover:bg-[#FAFAFA] ${
              selected === p.key ? "bg-[#FEF3E7]" : ""
            }`}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ backgroundColor: p.color }}
              />
              <span className="truncate text-[13px] text-[#374151]">
                {p.name}
              </span>
            </span>
            <span
              className="flex h-3.5 overflow-hidden rounded"
              style={{ width: `${Math.max(4, (p.count / max) * 100)}%` }}
            >
              {p.severity.map((c, i) =>
                c ? (
                  <span
                    key={i}
                    title={`${t(
                      `caseDashboard.live.severity.${SEVERITY_LEVELS[i]}`
                    )}: ${c}`}
                    style={{ flexGrow: c, backgroundColor: SEVERITY_COLORS[i] }}
                  />
                ) : null
              )}
            </span>
            <span className="text-right text-[13px] font-semibold tabular-nums text-[#111827]">
              {p.count}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

/** Slide-over with one player's session. */
export const PlayerPanel = ({ stats, playerKey, replay, now, onClose }) => {
  const { t } = useTranslation();
  const ago = useAgo();
  const p = stats.players.find((x) => x.key === playerKey);
  const points = useMemo(
    () => stats.points.filter((x) => x.key === playerKey),
    [stats.points, playerKey]
  );
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!p) return null;
  const from = stats.started ?? points[0]?.t ?? stats.end;
  const yMax = Math.ceil((Math.max(60, p.maxG) * 1.08) / 20) * 20;

  const facts = [
    { icon: Zap, label: t("caseDashboard.live.impactsColumn"), value: p.count },
    {
      icon: Trophy,
      label: t("caseDashboard.live.kpi.biggest"),
      value: p.count ? gText(t, p.maxG) : "—",
      color: p.count ? SEVERITY_TEXT[severityIndex(p.maxG)] : undefined,
    },
    {
      icon: Radio,
      label: t("caseDashboard.live.lastHeard"),
      value: replay
        ? p.lastContact
          ? fmtTime(new Date(p.lastContact))
          : "—"
        : ago(p.lastContact, now),
    },
    {
      icon: Battery,
      label: t("caseDashboard.live.battery"),
      value: p.battery == null ? "—" : `${p.battery}%`,
    },
  ];

  // Portalled to <body>: the modal's open animation leaves a transform on its
  // box, which would otherwise make "fixed" mean "fixed to the modal".
  return createPortal(
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[1000] bg-[#111827]/10"
      />
      <motion.aside
        initial={{ x: 48, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 48, opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="fixed inset-y-0 right-0 z-[1001] flex w-full max-w-[460px] flex-col border-l border-[#F3F4F6] bg-white shadow-[-12px_0_32px_rgba(0,0,0,0.08)]"
      >
        <div className="flex items-start gap-3 border-b border-[#F3F4F6] px-5 py-4">
          <Avatar name={p.name} color={p.color} size={44} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[17px] font-bold text-[#111827]">
              {p.name || t("caseDashboard.live.unknownPlayer")}
            </p>
            <div className="mt-1 flex items-center gap-2">
              {p.removed ? (
                <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-[11px] font-medium text-[#6B7280]">
                  {t("caseDashboard.live.removed")}
                </span>
              ) : (
                <StatusPill status={p.status} />
              )}
              <span
                className="font-mono text-[11px] text-[#9CA3AF]"
                title={p.mac}
              >
                #{p.wearable || fmtShortMac(p.mac)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("caseDashboard.close")}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-3">
            {facts.map(({ icon: Icon, label, value, color }) => (
              <div key={label} className="rounded-lg bg-[#F8F9FA] px-3 py-2.5">
                <span className="flex items-center gap-1 text-[11px] text-[#6B7280]">
                  <Icon size={12} />
                  {label}
                </span>
                <span
                  className="mt-0.5 block text-[18px] font-bold tabular-nums text-[#111827]"
                  style={color ? { color } : undefined}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>

          <h3 className="mb-2 mt-5 text-[13px] font-semibold text-[#111827]">
            {t("caseDashboard.live.theirSession")}
          </h3>
          <div className="h-44 rounded-lg border border-[#F3F4F6] px-1 pt-2">
            {points.length === 0 ? (
              <p className="pt-14 text-center text-[13px] text-[#9CA3AF]">
                {t("caseDashboard.live.noImpacts")}
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ top: 4, right: 12, bottom: 0, left: 0 }}
                >
                  {SEVERITY_BANDS.map((lo, i) => (
                    <ReferenceArea
                      key={lo}
                      y1={lo}
                      y2={SEVERITY_BANDS[i + 1] ?? yMax}
                      fill={["#FFFBEB", "#FFF7ED", "#FEF2F2"][i]}
                      fillOpacity={1}
                    />
                  ))}
                  <CartesianGrid stroke="#F3F4F6" vertical={false} />
                  <XAxis
                    dataKey="t"
                    type="number"
                    domain={[from, stats.end]}
                    tickFormatter={(v) => fmtTime(new Date(v))}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    tickLine={false}
                    minTickGap={30}
                  />
                  <YAxis
                    dataKey="g"
                    type="number"
                    domain={[0, yMax]}
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    tickLine={false}
                    axisLine={false}
                    width={36}
                  />
                  {!replay &&
                    p.lastContact &&
                    (p.status === "silent" || p.status === "quiet") && (
                      <ReferenceLine
                        x={p.lastContact}
                        stroke="#DC2626"
                        strokeDasharray="4 3"
                        label={{
                          value: t("caseDashboard.live.lastHeardMark"),
                          position: "insideTopLeft",
                          fill: "#DC2626",
                          fontSize: 10,
                        }}
                      />
                    )}
                  <Scatter
                    data={points}
                    isAnimationActive={false}
                    shape={({ cx, cy, payload }) => (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={3 + payload.sev}
                        fill={SEVERITY_COLORS[payload.sev]}
                        fillOpacity={0.85}
                        stroke="#fff"
                        strokeWidth={1}
                      />
                    )}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="mt-4">
            <SeverityBar counts={p.severity} height={8} />
            <div className="mt-2 flex justify-around gap-2 text-center">
              {SEVERITY_LEVELS.map((level, i) =>
                i === 0 && !p.severity[0] ? null : (
                  <div key={level}>
                    <span
                      className="block text-[15px] font-bold tabular-nums"
                      style={{ color: SEVERITY_TEXT[i] }}
                    >
                      {p.severity[i]}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
                      {t(`caseDashboard.live.severity.${level}`)}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>

          <h3 className="mb-1 mt-5 text-[13px] font-semibold text-[#111827]">
            {t("caseDashboard.live.theirImpacts")}
          </h3>
          <div className="divide-y divide-[#F3F4F6]">
            {[...points]
              .reverse()
              .slice(0, 60)
              .map((x) => (
                <div
                  key={x.id}
                  className="flex items-center justify-between py-2"
                >
                  <span className="flex items-center gap-2 text-[12px] text-[#6B7280]">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: SEVERITY_COLORS[x.sev] }}
                    />
                    {fmtTime(new Date(x.t))}
                    {!replay && (
                      <span className="text-[#D1D5DB]">· {ago(x.t, now)}</span>
                    )}
                  </span>
                  <span
                    className="text-[13px] font-semibold tabular-nums"
                    style={{ color: SEVERITY_TEXT[x.sev] }}
                  >
                    {gText(t, x.g)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </motion.aside>
    </>,
    document.body
  );
};
